/*
  Independent facets/filtering controller for the "Collection Product List"
  (custom-product-list-collection.liquid) section.

  This is a section-scoped fork of Dawn's facets.js. The stock facets.js
  targets fixed, unscoped IDs (#product-grid, #FacetFiltersForm, #ProductGridContainer, ...)
  which assumes exactly one filterable section per page. When this custom
  section is rendered on the same page as main-collection-product-grid.liquid,
  those fixed IDs collide and filtering in one section ends up driving the
  other section's markup. Every ID/selector here is namespaced with the
  section's own id (read from data-section-id) so each instance of this
  section operates independently.
*/

class CustomFacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector(`#CustomFacetsWrapperDesktop-${this.sectionId}`);
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const anyInstance = document.querySelector('custom-facet-filters-form');
      if (!anyInstance) return;
      const sectionId = anyInstance.dataset.sectionId;
      const searchParams = event.state ? event.state.searchParams : CustomFacetFiltersForm.searchParamsInitial;
      if (searchParams === CustomFacetFiltersForm.searchParamsPrev) return;
      CustomFacetFiltersForm.renderPage(sectionId, searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(sectionId, disable = true) {
    document.querySelectorAll(`[data-custom-facets-scope="${sectionId}"] .js-facet-remove`).forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(sectionId, searchParams, event, updateURLHash = true) {
    CustomFacetFiltersForm.searchParamsPrev = searchParams;
    const scope = document.querySelector(`[data-custom-facets-scope="${sectionId}"]`);
    if (!scope) return;

    const countContainer = document.getElementById(`CustomProductCount-${sectionId}`);
    const countContainerDesktop = document.getElementById(`CustomProductCountDesktop-${sectionId}`);
    const loadingSpinners = scope.querySelectorAll(
      '.facets-container .loading__spinner, custom-facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));

    const gridContainer = document.getElementById(`CustomProductGridContainer-${sectionId}`);
    const collectionEl = gridContainer ? gridContainer.querySelector('.collection') : null;
    if (collectionEl) collectionEl.classList.add('loading');
    if (countContainer) countContainer.classList.add('loading');
    if (countContainerDesktop) countContainerDesktop.classList.add('loading');

    const url = `${window.location.pathname}?section_id=${sectionId}&${searchParams}`;
    const filterDataUrl = (element) => element.url === url;

    CustomFacetFiltersForm.filterData.some(filterDataUrl)
      ? CustomFacetFiltersForm.renderSectionFromCache(sectionId, filterDataUrl, event)
      : CustomFacetFiltersForm.renderSectionFromFetch(sectionId, url, event);

    if (updateURLHash) CustomFacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(sectionId, url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        CustomFacetFiltersForm.filterData = [...CustomFacetFiltersForm.filterData, { html, url }];
        CustomFacetFiltersForm.renderFilters(sectionId, html, event);
        CustomFacetFiltersForm.renderProductGridContainer(sectionId, html);
        CustomFacetFiltersForm.renderProductCount(sectionId, html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(sectionId, filterDataUrl, event) {
    const html = CustomFacetFiltersForm.filterData.find(filterDataUrl).html;
    CustomFacetFiltersForm.renderFilters(sectionId, html, event);
    CustomFacetFiltersForm.renderProductGridContainer(sectionId, html);
    CustomFacetFiltersForm.renderProductCount(sectionId, html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(sectionId, html) {
    const containerId = `CustomProductGridContainer-${sectionId}`;
    const container = document.getElementById(containerId);
    if (!container) return;

    const fetchedContainer = new DOMParser().parseFromString(html, 'text/html').getElementById(containerId);
    if (!fetchedContainer) return;

    container.innerHTML = fetchedContainer.innerHTML;

    container.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });
  }

  static renderProductCount(sectionId, html) {
    const countId = `CustomProductCount-${sectionId}`;
    const countDesktopId = `CustomProductCountDesktop-${sectionId}`;
    const fetchedCount = new DOMParser().parseFromString(html, 'text/html').getElementById(countId);
    const container = document.getElementById(countId);
    const containerDesktop = document.getElementById(countDesktopId);

    if (fetchedCount && container) {
      container.innerHTML = fetchedCount.innerHTML;
      container.classList.remove('loading');
    }
    if (fetchedCount && containerDesktop) {
      containerDesktop.innerHTML = fetchedCount.innerHTML;
      containerDesktop.classList.remove('loading');
    }

    const scope = document.querySelector(`[data-custom-facets-scope="${sectionId}"]`);
    if (scope) {
      const loadingSpinners = scope.querySelectorAll(
        '.facets-container .loading__spinner, custom-facet-filters-form .loading__spinner'
      );
      loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
    }
  }

  static renderFilters(sectionId, html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const formSelector = `#CustomFacetFiltersForm-${sectionId} .js-filter, #CustomFacetFiltersFormMobile-${sectionId} .js-filter, #CustomFacetFiltersPillsForm-${sectionId} .js-filter`;
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(formSelector);
    const facetDetailsElementsFromDom = document.querySelectorAll(formSelector);

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    CustomFacetFiltersForm.renderActiveFacets(sectionId, parsedHTML);
    CustomFacetFiltersForm.renderAdditionalElements(sectionId, parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        CustomFacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        CustomFacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(sectionId, html) {
    const scope = document.querySelector(`[data-custom-facets-scope="${sectionId}"]`);
    if (!scope) return;

    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(`[data-custom-facets-scope="${sectionId}"] ${selector}`);
      const target = scope.querySelector(selector);
      if (!activeFacetsElement || !target) return;
      target.innerHTML = activeFacetsElement.innerHTML;
    });

    CustomFacetFiltersForm.toggleActiveFacets(sectionId, false);
  }

  static renderAdditionalElements(sectionId, html) {
    const scope = document.querySelector(`[data-custom-facets-scope="${sectionId}"]`);
    const fetchedScope = html.querySelector(`[data-custom-facets-scope="${sectionId}"]`);
    if (!scope || !fetchedScope) return;

    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const source = fetchedScope.querySelector(selector);
      const target = scope.querySelector(selector);
      if (!source || !target) return;
      target.innerHTML = source.innerHTML;
    });

    const mobileForm = document.getElementById(`CustomFacetFiltersFormMobile-${sectionId}`);
    const drawer = mobileForm ? mobileForm.closest('menu-drawer') : null;
    if (drawer) drawer.bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    CustomFacetFiltersForm.renderPage(this.sectionId, searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll(
      `[data-custom-facets-scope="${this.sectionId}"] custom-facet-filters-form form`
    );
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === `CustomFacetFiltersFormMobile-${this.sectionId}`;

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (
            form.id === `CustomSortForm-${this.sectionId}` ||
            form.id === `CustomFacetFiltersForm-${this.sectionId}` ||
            form.id === `CustomFacetSortDrawerForm-${this.sectionId}`
          ) {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === `CustomFacetFiltersFormMobile-${this.sectionId}`) {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    CustomFacetFiltersForm.toggleActiveFacets(this.sectionId);
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    CustomFacetFiltersForm.renderPage(this.sectionId, url);
  }
}

CustomFacetFiltersForm.filterData = [];
CustomFacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
CustomFacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('custom-facet-filters-form', CustomFacetFiltersForm);
CustomFacetFiltersForm.setListeners();

class CustomFacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('custom-facet-filters-form') || document.querySelector('custom-facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('custom-facet-remove', CustomFacetRemove);

if (!customElements.get('price-range')) {
  class PriceRange extends HTMLElement {
    constructor() {
      super();
      this.querySelectorAll('input').forEach((element) => {
        element.addEventListener('change', this.onRangeChange.bind(this));
        element.addEventListener('keydown', this.onKeyDown.bind(this));
      });
      this.setMinAndMaxValues();
    }

    onRangeChange(event) {
      this.adjustToValidValues(event.currentTarget);
      this.setMinAndMaxValues();
    }

    onKeyDown(event) {
      if (event.metaKey) return;

      const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
      if (!event.key.match(pattern)) event.preventDefault();
    }

    setMinAndMaxValues() {
      const inputs = this.querySelectorAll('input');
      const minInput = inputs[0];
      const maxInput = inputs[1];
      if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
      if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
      if (minInput.value === '') maxInput.setAttribute('data-min', 0);
      if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
    }

    adjustToValidValues(input) {
      const value = Number(input.value);
      const min = Number(input.getAttribute('data-min'));
      const max = Number(input.getAttribute('data-max'));

      if (value < min) input.value = min;
      if (value > max) input.value = max;
    }
  }

  customElements.define('price-range', PriceRange);
}
